import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  FormHelperText,
  OutlinedInput,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import OverLayLoader from '../OverLayLoader';
import LightTooltip from '../LightTooltip';
import LayoutTooltip from '../LayoutTooltip';
import InfoIcon from '../InfoIcon';
import HighlightedInput from '../HighlightedInput';
import {
  checkReplacement,
  getReplacementType,
  getReplacementTypes,
} from '../../services';
import { useAuth } from '../../context/auth/AuthContext';
import { CONSTANT_KEYS, REPLACEMENTS_KEYS } from '../../helpers/tableKeys';
import { createErrorNotification } from '../../utils/createErrorNotification';

const { WORDS, LANGUAGE, LANGUAGE_NAME, PROFILE_NAME, WORD_AFTER_CLEANING } =
  CONSTANT_KEYS;
const { COMMENT, REPLACED, REPLACEMENT, REPLACEMENT_TYPE } = REPLACEMENTS_KEYS;

// типы, недоступные для ручного выбора в поле Тип
const NON_EDITABLE_REPLACEMENT_TYPES = [
  'WORD',
  'CODE',
  'PHRASE',
  'PHRASE_SEP',
  'PHRASE_EXC',
  'SENTENCE_SEP',
  'SENTENCE_EXC',
];
const WORD_TYPE = 'WORD';
const REPLACED_PATTERN = /^(?=.*[A-Za-zА-Яа-яЁё0-9])/;
const SINGLE_WORD_PATTERN = /^[A-Za-zА-Яа-яЁё0-9]+$/;
const SECTION_SIGN = '§';

const EMPTY_INPUT = {
  [REPLACED]: '',
  [REPLACEMENT]: '',
  [COMMENT]: '',
};

const normalizeForCheck = value => value.trim().toLowerCase();

const AddReplacementForm = ({ currentLanguage, onSubmit, onCancel }) => {
  // то, что пользователь ввёл в форму (до нажатия "Проверить")
  const [inputValues, setInputValues] = useState(EMPTY_INPUT);
  const [fieldErrors, setFieldErrors] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // результат нажатия "Проверить" (/repl_clean) — то, что уходит при сохранении
  const [replacedResult, setReplacedResult] = useState(null);
  const [replacementResult, setReplacementResult] = useState(null);

  // тип, определённый бэкендом по результату проверки — от него зависит,
  // доступен ли Select для ручного изменения (только если пришёл WORD)
  const [replacementTypeResult, setReplacementTypeResult] = useState(null);
  // тип, который в итоге будет сохранён (изначально равен replacementTypeResult)
  const [selectedReplacementType, setSelectedReplacementType] = useState('');

  const [typeOptions, setTypeOptions] = useState([]);

  const { user } = useAuth();
  const { t } = useTranslation(['replacements', 'promptbar', 'common']);

  useEffect(() => {
    const fetchTypeOptions = async () => {
      try {
        const result = await getReplacementTypes();
        if (result) {
          setTypeOptions(result.data);
        }
      } catch (requestError) {
        createErrorNotification();
        console.log(requestError);
      }
    };

    fetchTypeOptions();
  }, []);

  const profileName =
    user?.profiles?.find(profile => profile.is_default)?.profile_name ??
    user?.profiles?.[0]?.profile_name;

  const schema = Yup.object().shape({
    [REPLACED]: Yup.string()
      .matches(REPLACED_PATTERN, t('promptbar:incorrectFormat'))
      .required(t('promptbar:requiredField')),
  });

  const isReplacedValid = REPLACED_PATTERN.test(inputValues[REPLACED].trim());
  const hasSectionSign =
    replacedResult?.[WORD_AFTER_CLEANING]?.includes(SECTION_SIGN);
  const hasNondictWords = Boolean(replacementResult?.nondict);
  const isSelectEditable = replacementTypeResult === WORD_TYPE;
  const needsSpecificReplacementType = selectedReplacementType === WORD_TYPE;
  const isSaveDisabled =
    !isReplacedValid ||
    hasSectionSign ||
    hasNondictWords ||
    needsSpecificReplacementType;

  const resetCheckResult = () => {
    setIsChecked(false);
    setReplacedResult(null);
    setReplacementResult(null);
    setReplacementTypeResult(null);
    setSelectedReplacementType('');
  };

  const handleFormSubmit = e => {
    e.preventDefault();

    const newData = {
      language_id: currentLanguage.id,
      replaced: replacedResult?.[WORD_AFTER_CLEANING],
      replacement: replacementResult?.[WORD_AFTER_CLEANING] ?? '',
      comment: inputValues[COMMENT],
      [REPLACEMENT_TYPE]: selectedReplacementType,
    };

    onSubmit(newData);
  };

  const handleChange = event => {
    const { value, name } = event.target;

    removeFieldError(name);

    if (name !== COMMENT) {
      resetCheckResult();
    }

    setInputValues(prev => ({ ...prev, [name]: value }));
  };

  const removeFieldError = name => {
    if (!fieldErrors[name]) {
      return;
    }
    const nextFieldErrors = { ...fieldErrors };
    delete nextFieldErrors[name];
    setFieldErrors(nextFieldErrors);
  };

  const handleValidate = async values => {
    try {
      await schema.validate(values, { abortEarly: false });
    } catch (errors) {
      const validationError = {};

      errors.inner.forEach(error => {
        validationError[error.path] = error.message;
      });
      setFieldErrors(prev => ({ ...prev, ...validationError }));
    }
  };

  const handleCheck = async () => {
    try {
      setIsLoading(true);
      setFieldErrors({});

      const isReplacedValid = await schema.isValid({
        [REPLACED]: inputValues[REPLACED].trim(),
      });

      if (!isReplacedValid) {
        return;
      }

      const normalizedReplaced = normalizeForCheck(inputValues[REPLACED]);
      const hasReplacement = Boolean(inputValues[REPLACEMENT]?.trim());
      const normalizedReplacement = hasReplacement
        ? normalizeForCheck(inputValues[REPLACEMENT])
        : '';

      const isReplacedSingleWord = SINGLE_WORD_PATTERN.test(normalizedReplaced);
      const isReplacementEqualToReplaced =
        hasReplacement &&
        SINGLE_WORD_PATTERN.test(normalizedReplacement) &&
        normalizedReplacement === normalizedReplaced;

      const wordsToClean = hasReplacement
        ? [normalizedReplaced, normalizedReplacement]
        : [normalizedReplaced];
      const result = await checkReplacement({
        [WORDS]: wordsToClean,
        [LANGUAGE_NAME]: currentLanguage.name,
        [PROFILE_NAME]: profileName,
      });

      if (!result) {
        return;
      }

      const newReplacedResult = isReplacedSingleWord
        ? { [WORD_AFTER_CLEANING]: normalizedReplaced }
        : result.data[0];
      const newReplacementResult = !hasReplacement
        ? null
        : isReplacementEqualToReplaced
          ? { [WORD_AFTER_CLEANING]: normalizedReplacement }
          : result.data[1];

      setReplacedResult(newReplacedResult);
      setReplacementResult(newReplacementResult);
      setIsChecked(true);

      if (!newReplacementResult?.nondict) {
        const typeResult = await getReplacementType({
          [REPLACED]: newReplacedResult?.[WORD_AFTER_CLEANING],
          [REPLACEMENT]: newReplacementResult?.[WORD_AFTER_CLEANING] ?? '',
        });
        if (typeResult) {
          setReplacementTypeResult(typeResult.data);
          setSelectedReplacementType(typeResult.data);
        }
      }
    } catch (requestError) {
      createErrorNotification();
      console.log(requestError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" autoComplete="off">
      {isLoading && <OverLayLoader />}
      <FormControl fullWidth variant="standard">
        <InputLabel htmlFor="TextField-0" shrink={false}>
          {t('common:dictionaryLanguage')}
        </InputLabel>
        <TextField
          id="TextField-0"
          disabled
          name={LANGUAGE}
          value={currentLanguage.name.toUpperCase()}
        />
      </FormControl>

      <FormControl
        fullWidth
        variant="standard"
        sx={{ mt: '2.5rem' }}
        error={Boolean(fieldErrors[REPLACED])}
      >
        <InputLabel htmlFor="TextField-1" shrink={false}>
          {t('table.replaced')}
          <LightTooltip
            title={<LayoutTooltip lang={currentLanguage.name} />}
            arrow
            placement="right"
          >
            <InfoIcon />
          </LightTooltip>
        </InputLabel>
        <HighlightedInput
          value={inputValues[REPLACED]}
          onChange={handleChange}
          lang={currentLanguage.name}
          error={Boolean(fieldErrors[REPLACED])}
          minRows={1}
          inputProps={{
            id: 'TextField-1',
            autoFocus: true,
            name: REPLACED,
            onKeyDown: event => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            },
            onBlur: () => handleValidate(inputValues),
          }}
        />
        {fieldErrors[REPLACED] && (
          <FormHelperText>{fieldErrors[REPLACED]}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel htmlFor="TextField-2" shrink={false}>
          {t('table.replacement')}
          <LightTooltip
            title={<LayoutTooltip lang={currentLanguage.name} />}
            arrow
            placement="right"
          >
            <InfoIcon />
          </LightTooltip>
        </InputLabel>
        <HighlightedInput
          value={inputValues[REPLACEMENT]}
          onChange={handleChange}
          lang={currentLanguage.name}
          minRows={1}
          inputProps={{
            id: 'TextField-2',
            name: REPLACEMENT,
            onKeyDown: event => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            },
          }}
        />
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel htmlFor="TextField-3" shrink={false}>
          {t('table.comment')}
        </InputLabel>
        <TextField
          id="TextField-3"
          name={COMMENT}
          value={inputValues[COMMENT]}
          onChange={handleChange}
        />
      </FormControl>

      <Box sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}>
        <Button
          type="button"
          variant="contained"
          onClick={handleCheck}
          disabled={!isReplacedValid}
        >
          {t('promptbar:check')}
        </Button>
      </Box>

      {isChecked && (
        <Box sx={{ mt: '2.5rem' }}>
          <FormControl fullWidth variant="standard" error={hasSectionSign}>
            <InputLabel htmlFor="TextField-4" shrink={false}>
              {t('table.replaced')}
            </InputLabel>
            <TextField
              disabled
              id="TextField-4"
              name="replaced_result"
              value={replacedResult?.[WORD_AFTER_CLEANING] ?? ''}
              error={hasSectionSign}
              helperText={
                hasSectionSign &&
                t(
                  'Replace the parts of the string before and after § separately',
                  {
                    ns: 'promptbar',
                  },
                )
              }
            />
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
            <InputLabel htmlFor="TextField-5" shrink={false}>
              {t('table.replacement')}
            </InputLabel>
            <TextField
              disabled
              id="TextField-5"
              name="replacement_result"
              value={replacementResult?.[WORD_AFTER_CLEANING] ?? ''}
            />
          </FormControl>

          {hasNondictWords && (
            <FormControl fullWidth variant="standard" sx={{ mt: 5 }}>
              <InputLabel htmlFor="TextField-6" shrink={false}>
                {t('Non-dictionary words', { ns: 'common' })}
              </InputLabel>
              <TextField
                disabled
                id="TextField-6"
                name="nondict"
                value={replacementResult.nondict || ''}
                helperText={t(
                  'Add these words to the dictionary of replacements',
                  {
                    ns: 'promptbar',
                  },
                )}
                error={true}
              />
            </FormControl>
          )}

          {!hasNondictWords && (
            <FormControl
              fullWidth
              variant="standard"
              sx={{ mt: '2.5rem' }}
              error={needsSpecificReplacementType}
            >
              <InputLabel id="Select-replacement-type-label" shrink={false}>
                {t('table.replacement_type')}
              </InputLabel>
              <Select
                labelId="Select-replacement-type-label"
                id="Select-replacement-type"
                name={REPLACEMENT_TYPE}
                value={selectedReplacementType}
                disabled={!isSelectEditable}
                onChange={e => setSelectedReplacementType(e.target.value)}
                input={<OutlinedInput label={t('table.replacement_type')} />}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ size: 'small', autoFocus: false }}
              >
                {typeOptions.map(option => (
                  <MenuItem
                    key={option}
                    value={option}
                    disabled={NON_EDITABLE_REPLACEMENT_TYPES.includes(option)}
                  >
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
              {needsSpecificReplacementType && (
                <FormHelperText>
                  {t('Select a specific replacement type', {
                    ns: 'promptbar',
                  })}
                </FormHelperText>
              )}
            </FormControl>
          )}

          <Box sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}>
            <Button
              type="button"
              color="secondary"
              sx={{ mr: 1 }}
              variant="contained"
              onClick={onCancel}
            >
              {t('promptbar:cancel')}
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleFormSubmit}
              disabled={isSaveDisabled}
            >
              {t('promptbar:save')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

AddReplacementForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  currentLanguage: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
};

export default AddReplacementForm;
